import { useEffect, useState } from "react";
import InputFactory from "../InputFactory";
import { getAllYears } from "./utils";

const CATEGORY_ICONS = {
  Monthly: "bi bi-calendar3",
  Quarterly: "bi bi-calendar3-range",
  "Bi-Yearly": "bi bi-calendar2-half",
  Yearly: "bi bi-calendar4",
};

function DatesFilter({
  category,
  year,
  month,
  onChange,
  months,
  onFilter,
  quarter,
  half,
  onChangeFilter,
  showApplyButton = false,
  variant = "card",
  title = "Filter by date",
}) {
  const [filters, setFilters] = useState({
    category: category || "",
    year: year || "",
    month: month || "",
    quarter: quarter || "",
    half: half || "",
  });

  const notifyChange = (value, field) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (onChangeFilter) {
      onChangeFilter(value, field);
    } else if (onChange) {
      onChange({ [field]: value });
    }
  };

  useEffect(() => {
    if (showApplyButton) return;

    const shouldFilter = () => {
      if (!filters.category || !filters.year) return false;
      switch (filters.category) {
        case "Monthly":
          return !!filters.month;
        case "Bi-Yearly":
          return !!filters.half;
        case "Quarterly":
          return !!filters.quarter;
        case "Yearly":
          return true;
        default:
          return false;
      }
    };

    if (shouldFilter()) onFilter?.();
  }, [
    showApplyButton,
    filters.category,
    filters.year,
    filters.month,
    filters.quarter,
    filters.half,
  ]);

  const renderThirdFilter = () => {
    switch (filters.category) {
      case "Monthly":
        return (
          <InputFactory
            type="Enum"
            options={months}
            className={variant === "inline" ? "bg-light-subtle" : "bg-white"}
            placeholder={variant === "inline" ? "Select" : "Select Month"}
            field="month"
            defaultValue={month}
            onChange={notifyChange}
            required
            clearable={false}
          />
        );
      case "Bi-Yearly":
        return (
          <InputFactory
            type="Enum"
            options={["1st Half", "2nd Half"]}
            className={variant === "inline" ? "bg-light-subtle" : "bg-white"}
            placeholder={variant === "inline" ? "Select" : "Select Half"}
            field="half"
            defaultValue={half}
            onChange={notifyChange}
            required
            clearable={false}
          />
        );
      case "Quarterly":
        return (
          <InputFactory
            type="Enum"
            options={[
              "1st Quarter",
              "2nd Quarter",
              "3rd Quarter",
              "4th Quarter",
            ]}
            className={variant === "inline" ? "bg-light-subtle" : "bg-white"}
            placeholder={variant === "inline" ? "Select" : "Select Quarter"}
            field="quarter"
            defaultValue={quarter}
            onChange={notifyChange}
            required
            clearable={false}
          />
        );
      default:
        return null;
    }
  };

  const content = (
    <>
      {/* Card header */}
      {variant === "card" && (
        <div
          className="d-flex align-items-center justify-content-between pb-2 px-3 py-2 overflow-hidden rounded-top-3"
          style={{
            borderBottom: "1px solid #f0f0f0",
            backgroundColor: "#fafafa",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-2"
              style={{
                width: "30px",
                height: "30px",
                backgroundColor: "#eef2ff",
              }}
            >
              <i
                className={
                  filters.category
                    ? CATEGORY_ICONS[filters.category]
                    : "bi bi-calendar3"
                }
                style={{ color: "#4b5c7c", fontSize: "13px" }}
              />
            </div>
            <h6
              className="m-0 fw-semibold"
              style={{
                color: "#283754",
                fontSize: "0.875rem",
                letterSpacing: "0.3px",
              }}
            >
              {title}
            </h6>
          </div>

          {filters.category && (
            <span
              className="badge rounded-pill d-flex align-items-center gap-1"
              style={{
                backgroundColor: "#eef2ff",
                color: "#4b5c7c",
                fontSize: "11px",
                fontWeight: 600,
              }}
            >
              <i
                className={CATEGORY_ICONS[filters.category]}
                style={{ fontSize: "10px" }}
              />
              {filters.category}
            </span>
          )}
        </div>
      )}

      <div className="d-flex flex-column gap-2 p-3">
        {variant === "card" && (
          <label
            className="text-muted fw-medium d-flex align-items-center gap-1"
            style={{
              fontSize: "11px",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
            }}
          >
            <i className="bi bi-sliders" style={{ fontSize: "10px" }} />
            View by
          </label>
        )}

        <div
          className={
            variant === "inline"
              ? "d-flex flex-wrap gap-2 align-items-md-center"
              : "d-flex flex-column flex-md-row gap-2 flex-grow-1"
          }
        >
          <div className={variant === "card" ? "flex-fill" : ""}>
            <InputFactory
              type="Enum"
              options={["Monthly", "Quarterly", "Bi-Yearly", "Yearly"]}
              defaultValue={category}
              className={variant === "inline" ? "bg-light-subtle" : "bg-white"}
              placeholder="Select Category"
              field="category"
              onChange={notifyChange}
              required
              clearable={false}
            />
          </div>

          <div className={variant === "card" ? "flex-fill" : ""}>
            <InputFactory
              type="Enum"
              options={getAllYears()}
              defaultValue={`${year}`}
              className={variant === "inline" ? "bg-light-subtle" : "bg-white"}
              placeholder="Select Year"
              field="year"
              onChange={notifyChange}
              required
              clearable={false}
            />
          </div>

          {filters.category !== "Yearly" && (
            <div className={variant === "card" ? "flex-fill" : ""}>
              {renderThirdFilter()}
            </div>
          )}

          {showApplyButton && (
            <button
              type="submit"
              className="btn d-flex align-items-center gap-2 text-white fw-medium"
              style={{
                backgroundColor: "#283754",
                borderRadius: "8px",
                padding: "6px 16px",
                boxShadow: "0 2px 6px rgba(40, 55, 84, 0.3)",
                whiteSpace: "nowrap",
              }}
            >
              <i className="bi bi-check2-circle" style={{ fontSize: "14px" }} />
              Apply Filters
            </button>
          )}
        </div>
      </div>
    </>
  );

  if (variant === "card") {
    return (
      <div
        className="d-flex flex-column bg-white rounded-3 border border-dark-subtle shadow-sm"
        style={{ border: "1px solid #e9ecef" }}
      >
        {content}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
      {content}
    </div>
  );
}

export default DatesFilter;
